import React from 'react';
import { Building2, GraduationCap, Dumbbell, Shield, Brain, HeartPulse, FileText, Newspaper, AlertOctagon, Download, ExternalLink, PieChart, Briefcase, Network, UserCheck } from 'lucide-react';

/**
 * VaultInstitutionalBriefs — pre-populated content blocks that sit ATOP the
 * existing Offers / Acquisitions / Fundraise / PR & Social CRUD interfaces.
 *
 * Everything below is purely presentational — investors get an instant,
 * institutional-grade narrative the moment they open a tab, while Robert's
 * existing live ledgers continue to live underneath each brief.
 *
 * Brand language:
 *   • Warm pastel — coral / mint / lavender / amber on cream
 *   • Always lead with a 1-line thesis, then bullets, then a CTA tag-row
 *   • Typography mirrors the public /investors gateway
 */

const PALETTE = {
  cream:    '#FAFAED',
  border:   'rgba(92,82,106,0.10)',
  text:     '#2A2235',
  muted:    '#5C526A',
  coral:    '#F99B7D',
  mint:     '#A3E2C9',
  lavender: '#9BB8ED',
  amber:    '#F5C84B',
  plum:     '#B392AC',
};

// ── Shared sub-components ─────────────────────────────────────────────────

export const BriefShell = ({ eyebrow, title, lede, accent = PALETTE.coral, testId, children }) => (
  <section
    className="rounded-3xl p-6 lg:p-8"
    style={{ background: '#FFFFFF', border: `1px solid ${PALETTE.border}`, borderTop: `4px solid ${accent}` }}
    data-testid={testId}
  >
    {eyebrow && (
      <div className="text-xs font-black uppercase tracking-[0.18em] mb-2" style={{ color: accent }}>{eyebrow}</div>
    )}
    <h2 className="text-2xl lg:text-3xl font-black leading-tight mb-2" style={{ color: PALETTE.text }}>{title}</h2>
    {lede && <p className="text-sm mb-5" style={{ color: PALETTE.muted, maxWidth: '60ch' }}>{lede}</p>}
    {children}
  </section>
);

const BriefCard = ({ icon, iconBg, title, body, footer, testId }) => (
  <div
    className="rounded-2xl p-5"
    style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}` }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2.5 mb-2.5">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
        {icon}
      </div>
      <h3 className="text-sm font-bold" style={{ color: PALETTE.text }}>{title}</h3>
    </div>
    <div className="text-sm leading-relaxed" style={{ color: PALETTE.muted }}>{body}</div>
    {footer && <div className="mt-3 pt-3 border-t" style={{ borderColor: PALETTE.border }}>{footer}</div>}
  </div>
);

const Pill = ({ children, color = PALETTE.coral }) => (
  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold"
    style={{ background: color + '22', color: PALETTE.text, border: `1px solid ${color}55` }}>
    {children}
  </span>
);

// ── 1. Offers — B2B Partnership Track ──────────────────────────────────────

export const OffersInstitutionalBrief = () => (
  <BriefShell
    eyebrow="Strategic B2B Partnership Track"
    title="Active commercial pipeline — structured, repeatable, ready to sign"
    lede="HiBFF isn't theoretical. The five commercial blueprints below are the live anchors of our institutional revenue ladder — civic, wellness, corporate, enterprise, and provider — each pre-engineered for rapid signing across the regional rollout."
    accent={PALETTE.mint}
    testId="offers-brief"
  >
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
      <BriefCard
        icon={<GraduationCap size={16} style={{ color: '#1E6F4F' }} />}
        iconBg={PALETTE.mint + '44'}
        title="The B2B2C School Offer"
        testId="offers-brief-schools"
        body={
          <>
            Standardised annual licensing for schools worldwide at <strong>one global rate</strong> — <strong>$6.85 per student per month</strong>, billed annually (14-day free pilot; terms negotiated per school). Every licensed school introduces HiBFF to its parent community with institutional authority; parents join the <strong>$33/month</strong> consumer ecosystem directly, and the school gains a measurable youth-safety dividend.
          </>
        }
        footer={
          <div className="flex flex-wrap gap-1.5">
            <Pill color={PALETTE.mint}>5-year term</Pill>
            <Pill color={PALETTE.mint}>$1 / year</Pill>
            <Pill color={PALETTE.mint}>50% parent code</Pill>
            <Pill color={PALETTE.mint}>civic safeguarding KPI</Pill>
          </div>
        }
      />
      <BriefCard
        icon={<Dumbbell size={16} style={{ color: '#9A4A33' }} />}
        iconBg={PALETTE.coral + '33'}
        title="The Gym / Wellness Pilot"
        testId="offers-brief-wellness"
        body={
          <>
            Flat-fee SaaS license for <strong>regional youth athletic clubs and wellness centres</strong> — they unlock our gym-style character-challenge environment as an in-house teen-retention hook. Operator pays a quarterly tier (Bronze / Silver / Gold based on member count), HiBFF supplies branded onboarding kits, scoreboards, and parent-pairing flows.
          </>
        }
        footer={
          <div className="flex flex-wrap gap-1.5">
            <Pill color={PALETTE.coral}>3-tier pricing</Pill>
            <Pill color={PALETTE.coral}>quarterly billed</Pill>
            <Pill color={PALETTE.coral}>retention SLA</Pill>
            <Pill color={PALETTE.coral}>co-branded launch</Pill>
          </div>
        }
      />
      <BriefCard
        icon={<Briefcase size={16} style={{ color: '#1E3A6F' }} />}
        iconBg={PALETTE.lavender + '44'}
        title="Corporate 'Working Parents' Wellness"
        testId="offers-brief-corporate"
        body={
          <>
            Annual flat-fee corporate license that adds HiBFF to a company&apos;s <strong>employee wellness benefit stack</strong>. Every employee with a teen at home unlocks the Families Elite / Master tiers — turning family stress, screen-time battles, and teen anxiety into measurable employer-funded support. HR procures from <strong>pre-allocated mental-health budgets</strong>; sales cycle closes in weeks, not quarters.
          </>
        }
        footer={
          <div className="flex flex-wrap gap-1.5">
            <Pill color={PALETTE.lavender}>annual flat fee</Pill>
            <Pill color={PALETTE.lavender}>HR-procured</Pill>
            <Pill color={PALETTE.lavender}>family-paired</Pill>
            <Pill color={PALETTE.lavender}>expedited cycle</Pill>
          </div>
        }
      />
      <BriefCard
        icon={<Network size={16} style={{ color: '#8A6B14' }} />}
        iconBg={PALETTE.amber + '33'}
        title="White-Label Franchise Expansion"
        testId="offers-brief-whitelabel"
        body={
          <>
            Enterprise SaaS upgrade of the gym pilot. Major franchises — <strong>martial-arts chains, youth fitness networks, performance academies</strong> — license HiBFF and upload their own coaches or local branding as the visual template for our AI Practice Partners. One corporate contract drops HiBFF into <strong>50+ locations simultaneously</strong>, moving us from club-by-club selling into multi-site enterprise revenue.
          </>
        }
        footer={
          <div className="flex flex-wrap gap-1.5">
            <Pill color={PALETTE.amber}>white-label</Pill>
            <Pill color={PALETTE.amber}>custom AvAs</Pill>
            <Pill color={PALETTE.amber}>multi-site</Pill>
            <Pill color={PALETTE.amber}>enterprise SaaS</Pill>
          </div>
        }
      />
      <BriefCard
        icon={<UserCheck size={16} style={{ color: '#5C3A6F' }} />}
        iconBg={PALETTE.plum + '33'}
        title="Hybrid Coaching Provider License"
        testId="offers-brief-coaching"
        body={
          <>
            A <strong>&ldquo;HiBFF Provider License&rdquo;</strong> sold to independent life coaches, career mentors, and youth counsellors. They pay a monthly fee to assign our 20-challenge quest as homework between live sessions, and unlock a <strong>professional dashboard with replay analytics and tone-scoring</strong> on every client. Each provider becomes a force-multiplier — onboarding their own high-paying client books into the HiBFF platform.
          </>
        }
        footer={
          <div className="flex flex-wrap gap-1.5">
            <Pill color={PALETTE.plum}>monthly recurring</Pill>
            <Pill color={PALETTE.plum}>provider dashboard</Pill>
            <Pill color={PALETTE.plum}>client-replay</Pill>
            <Pill color={PALETTE.plum}>affiliate-effect</Pill>
          </div>
        }
      />
    </div>
    <p className="text-xs" style={{ color: PALETTE.muted }}>
      <Building2 size={11} className="inline mr-1" /> Live ledger of inbound bids and counter-offers continues below.
    </p>
  </BriefShell>
);

// ── 2. Acquisitions — Strategic Exit Thesis ────────────────────────────────

export const AcquisitionsInstitutionalBrief = () => (
  <BriefShell
    eyebrow="Defensive Exit Thesis"
    title="Three buyer pillars — and why we already fit each one"
    lede="Our 300,000+-line codebase (recounted live in the Codebase panel), three live UK patent applications (GB2617027.4 · GB2622892.4 · GB2622997.1), and Claude-Sonnet-4.5 safety wrapper are intentionally engineered as plug-and-play strategic assets. Three discrete buyer categories sit upstream — each with a different reason to want us first."
    accent={PALETTE.lavender}
    testId="acquisitions-brief"
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
      <BriefCard
        icon={<Shield size={16} style={{ color: '#1E3A6F' }} />}
        iconBg={PALETTE.lavender + '44'}
        title="Big Tech — Safety Absorption"
        testId="acq-brief-bigtech"
        body={
          <>
            Targets: <strong>Meta Family Center, Apple Screen Time, Google Family Link</strong>. Buying HiBFF lets them absorb a battle-tested, COPPA + SOC2-aligned youth-safety stack overnight — front-running the antitrust / minor-liability lawsuits already circling the sector.
          </>
        }
        footer={<Pill color={PALETTE.lavender}>regulatory shield</Pill>}
      />
      <BriefCard
        icon={<Brain size={16} style={{ color: '#5C3A6F' }} />}
        iconBg={PALETTE.plum + '33'}
        title="EdTech — Gamification Engine"
        testId="acq-brief-edtech"
        body={
          <>
            Targets: <strong>Duolingo, Age of Learning, Khan, Roblox Education</strong>. Our BIG5 / OCEAN psychometric onboarding + AvA personalisation engine slots into their pipeline as a teen-retention multiplier they cannot build internally inside an 18-month window.
          </>
        }
        footer={<Pill color={PALETTE.plum}>retention multiplier</Pill>}
      />
      <BriefCard
        icon={<HeartPulse size={16} style={{ color: '#9A4A33' }} />}
        iconBg={PALETTE.coral + '33'}
        title="Insurance / Wellness — Claims Mitigation"
        testId="acq-brief-insurance"
        body={
          <>
            Targets: <strong>UnitedHealth, Cigna, AXA, Bupa, Vitality</strong>. Validated, proactive adolescent mental-health tool — gamified habits that measurably reduce long-tail claims. Acquirers fold us in to underwrite preventive-care lines they currently cannot model.
          </>
        }
        footer={<Pill color={PALETTE.coral}>actuarial alpha</Pill>}
      />
    </div>
    <p className="text-xs" style={{ color: PALETTE.muted }}>
      <Building2 size={11} className="inline mr-1" /> Buyer relationship pipeline (CEOs, contact owners, last-touch metrics) tracked below.
    </p>
  </BriefShell>
);

// ── 3. Fundraise — Use of Funds Pastel Pie ─────────────────────────────────

const USE_OF_FUNDS = [
  { label: 'Engineering & AI Scaling',   pct: 45, color: PALETTE.coral,    note: 'Claude Sonnet 4.5 wrapper fine-tuning · NeMo guardrail optimisation · animated multi-modal AvA library' },
  { label: 'Institutional GTM',          pct: 35, color: PALETTE.mint,     note: 'Expanding the field team to lock 5-year HiBFF Gives school contracts across regional zip codes' },
  { label: 'Compliance & Safeguarding',  pct: 20, color: PALETTE.lavender, note: 'Continuous clinical red-teaming of crisis-triage thresholds (≥0.80 / ≥0.95) · SOC 2 Type II audits' },
];

const polarToCartesian = (cx, cy, r, angle) => ({
  x: cx + r * Math.cos((angle - 90) * Math.PI / 180),
  y: cy + r * Math.sin((angle - 90) * Math.PI / 180),
});

const arc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
};

export const FundraiseInstitutionalBrief = () => {
  let cursor = 0;
  const slices = USE_OF_FUNDS.map(s => {
    const start = cursor;
    cursor += (s.pct / 100) * 360;
    return { ...s, start, end: cursor };
  });

  return (
    <BriefShell
      eyebrow="Capital Deployment Timeline"
      title="Use of funds — disciplined, measurable, defensible"
      lede="Every dollar entering this round is pre-allocated across three institutional lines. No discretionary slush, no marketing vanity spend — the entire raise compounds the moat."
      accent={PALETTE.amber}
      testId="fundraise-brief"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        {/* Pastel donut */}
        <div className="md:col-span-5 flex items-center justify-center" data-testid="use-of-funds-chart">
          <svg viewBox="0 0 220 220" style={{ width: 220, height: 220 }}>
            <defs>
              <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="3" stdDeviation="3" floodOpacity="0.12" />
              </filter>
            </defs>
            <g filter="url(#softShadow)">
              {slices.map((s, i) => (
                <path
                  key={i}
                  d={arc(110, 110, 100, s.start, s.end)}
                  fill={s.color}
                  data-testid={`uof-slice-${i}`}
                />
              ))}
              <circle cx="110" cy="110" r="60" fill="#FFFFFF" />
            </g>
            <g>
              <text x="110" y="103" textAnchor="middle" style={{ fontSize: 11, fill: PALETTE.muted, fontWeight: 700, letterSpacing: 1.8 }}>USE OF</text>
              <text x="110" y="124" textAnchor="middle" style={{ fontSize: 18, fill: PALETTE.text, fontWeight: 900 }}>FUNDS</text>
            </g>
          </svg>
        </div>
        {/* Legend */}
        <div className="md:col-span-7 space-y-3" data-testid="use-of-funds-legend">
          {slices.map((s, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`uof-row-${i}`}>
              <span className="mt-1 inline-block w-3.5 h-3.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-bold" style={{ color: PALETTE.text }}>{s.label}</span>
                  <span className="text-lg font-black tabular-nums" style={{ color: s.color }}>{s.pct}%</span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>{s.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs mt-6" style={{ color: PALETTE.muted }}>
        <PieChart size={11} className="inline mr-1" /> Live dilution waterfall, round-by-round capital targets, and founder-position projections continue below.
      </p>
    </BriefShell>
  );
};

// ── 4. PR & Social — Public Authority Vault ────────────────────────────────

const MEDIA_KITS = [
  {
    id: 'safety',
    title: 'Safety & Ethics White Paper',
    body: 'How our Claude-4.5 guardrails actively block toxic prompts, separating safe-harbour HiBFF from generic unaligned chatbots. Full programmatic filtering taxonomy + crisis-triage threshold table.',
    icon: <Shield size={16} style={{ color: '#1E3A6F' }} />,
    iconBg: PALETTE.lavender + '44',
    pill: 'PDF · placeholder',
    pillColor: PALETTE.lavender,
    file: 'hibff-safety-ethics-white-paper.pdf',
  },
  {
    id: 'family',
    title: 'The Family Impact Kit',
    body: 'Pre-drafted press releases, high-res dual parent-teen dashboard screens, the co-signed Family Charter template, and a one-page explainer for journalists.',
    icon: <Newspaper size={16} style={{ color: '#1E6F4F' }} />,
    iconBg: PALETTE.mint + '44',
    pill: 'PDF · placeholder',
    pillColor: PALETTE.mint,
    file: 'hibff-family-impact-kit.pdf',
  },
  {
    id: 'crisis',
    title: 'Crisis Communication Protocol',
    body: 'Internal-only escalation playbook — how the system gracefully routes self-harm and mental-health queries to Samaritans + on-call clinicians. Tier-1 brand-equity shield.',
    icon: <AlertOctagon size={16} style={{ color: '#9A4A33' }} />,
    iconBg: PALETTE.coral + '33',
    pill: 'CONFIDENTIAL',
    pillColor: PALETTE.coral,
    file: 'hibff-crisis-communication-protocol.pdf',
  },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const PRInstitutionalBrief = () => (
  <BriefShell
    eyebrow="Public Authority Vault"
    title="Master media kits — institutional-grade communications shield"
    lede="HiBFF's public surface is intentionally disciplined. Each kit below is pre-loaded as a branded placeholder and ready for legal review or press release at a moment's notice."
    accent={PALETTE.amber}
    testId="pr-brief"
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
      {MEDIA_KITS.map(k => (
        <BriefCard
          key={k.id}
          icon={k.icon}
          iconBg={k.iconBg}
          title={k.title}
          testId={`pr-brief-${k.id}`}
          body={k.body}
          footer={
            <div className="flex items-center justify-between">
              <Pill color={k.pillColor}>{k.pill}</Pill>
              <a
                href={`${BACKEND_URL}/api/static/press-kits/${k.file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-bold hover:opacity-70 transition-opacity"
                style={{ color: PALETTE.text }}
                data-testid={`pr-brief-${k.id}-download`}
              >
                <Download size={11} /> Download
              </a>
            </div>
          }
        />
      ))}
    </div>
    <p className="text-xs" style={{ color: PALETTE.muted }}>
      <FileText size={11} className="inline mr-1" /> Activity log (posts, reach, engagement, AI recommendations) continues below.
    </p>
  </BriefShell>
);
